import { Center, Tabs, Text, Title } from '@mantine/core'
import { modals } from '@mantine/modals'
import { createFileRoute, redirect, useBlocker } from '@tanstack/react-router'
import ProductDetailCard from 'components/productDetails/ProductDetailCard'
import { productWithVariantsQueryOptions, useProductWithVariants } from 'hooks/products'
import { useEffect, useState } from 'react'
import { Category } from 'types/products'
import { categories, getCategoryName, getVariantTable } from 'utils'
import { create } from 'zustand'

type Store = {
  isModified: Boolean
  setIsModified: (value: boolean) => void
}

export const useIsProductModifiedStore = create<Store>((set) => ({
  isModified: false,
  setIsModified: (value: boolean) => set({ isModified: value }),
}))

export const Route = createFileRoute('/_auth/product/$productId/{-$section}')({
  loader: async ({ params, context: { queryClient } }) => {
    return await queryClient.ensureQueryData(productWithVariantsQueryOptions(params.productId))
  },
  component: RouteComponent,
  beforeLoad: (args) => {
    const { section } = args.params
    if (!section) {
      throw redirect({ to: `/product/${args.params.productId}/${Category.PREMADE}` })
    }
  },
})

function RouteComponent() {
  const { isModified, setIsModified } = useIsProductModifiedStore((state) => state)
  const [isLongOnPage, setIsLongOnPage] = useState(false)
  const { section } = Route.useParams()
  const navigate = Route.useNavigate()
  const { variant }: { variant: string | undefined } = Route.useSearch({ select: (s) => s ?? { variant: undefined } })

  useEffect(() => {
    setIsModified(false)
    const timeout = setTimeout(() => setIsLongOnPage(true), 20000)
    return () => clearTimeout(timeout)
  }, [])

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (current.params.productId === next.params.productId) {
        return false
      }
      return !isModified && isLongOnPage
    },
    withResolver: true,
    enableBeforeUnload: false,
  })
  const openModal = () => {
    modals.openConfirmModal({
      title: 'Produkt niepobrany z magazynu',
      children: <Text size="sm">Nie pobrałeś jeszcze tego produktu z magazynu. Czy na pewno chcesz kontynuować?</Text>,
      labels: { confirm: 'Tak, tylko sprawdzałem', cancel: 'Wróć' },
      centered: true,
      withCloseButton: false,
      closeOnClickOutside: false,
      onCancel: reset,
      onConfirm: proceed,
    })
  }

  useEffect(() => {
    if (status === 'blocked') {
      openModal()
    }
  }, [status])

  const { data: product } = useProductWithVariants(Route.useParams().productId)

  useEffect(() => {
    // is section doesn't have any variants, redirect to first available
    if (product) {
      const currentCategory = section as Category
      if (product.variants[currentCategory].length === 0) {
        const firstAvailableCategory = categories.find((category) => product.variants[category].length > 0)
        if (firstAvailableCategory) {
          navigate({ to: `../${firstAvailableCategory}`, replace: true })
        }
      }
    }
  }, [product])

  if (!product) return null

  const isNoVariants = categories.every((category) => product.variants[category].length === 0)

  return (
    <>
      <ProductDetailCard mt="xl" product={product} />
      {isNoVariants ? (
        <Center mt="xl">
          <Title order={2} c="dimmed" mt="xl" pt="xl" mb={0}>
            Brak wariantów produktu
          </Title>
        </Center>
      ) : (
        <Tabs value={section} onChange={(value) => value && navigate({ to: `../${value}` })} mt="xl">
          <Tabs.List>
            {categories.map((category) => {
              if (product.variants[category].length === 0) return null
              return (
                <Tabs.Tab value={category} key={category}>
                  {getCategoryName(category).header}
                </Tabs.Tab>
              )
            })}
          </Tabs.List>
          {categories.map((category) => {
            const VariantTable = getVariantTable(category)
            return (
              <Tabs.Panel value={category} key={category}>
                {/* @ts-ignore */}
                <VariantTable key={category} variants={product.variants[category]} highlightItemId={variant} />
              </Tabs.Panel>
            )
          })}
        </Tabs>
      )}
    </>
  )
}
